import {
  ApolloCache,
  DefaultContext,
  FetchResult,
  MutationFunctionOptions,
  OperationVariables,
  useMutation,
} from '@apollo/client'
import { AsStoreObject } from '@apollo/client/utilities'
import {
  AcceptQuoteMutation,
  CreateQuoteMutation,
  DeleteQuoteMutation,
  LegalMatterQuoteFragment,
  QuoteDeclineMutation,
  SendQuoteMutation,
  UpdateQuoteMutation,
  QuoteConnection,
} from '@generatedTypes/graphql'
import { gql } from '@generatedTypes/gql'
import { edgesInclude, getCacheId, makeEmptyData } from '@utils/useApolloClient'
import { filter } from 'lodash'

const acceptQuoteMutation = gql(/*GraphQL*/ `
  mutation acceptQuote($quoteKey: Key!) {
    quoteAccept(quoteKey: $quoteKey) {
      ... on QuoteAcceptMutationResponse {
        code
        message
        success
        clientSecret
        quote {
          ...LegalMatterQuote
        }
      }
      ... on MutationError {
        code
        message
        success
        code
        userErrors {
          fieldErrors {
            name
            errors {
              message
              code
            }
          }
        }
      }
    }
  }
`)

const createQuoteMutation = gql(/*GraphQL*/ `
  mutation createQuote($quoteData: QuoteCreateInput!) {
    quoteCreate(quoteData: $quoteData) {
      ... on QuoteMutationResponse {
        code
        message
        success
        quote {
          ...LegalMatterQuote
        }
      }
      ... on MutationError {
        code
        message
        success
        code
        userErrors {
          fieldErrors {
            name
            errors {
              message
              code
            }
          }
        }
      }
    }
  }
`)

const deleteQuoteMutation = gql(/*GraphQL*/ `
  mutation deleteQuote($quoteKey: Key!) {
    quoteDelete(quoteKey: $quoteKey) {
      code
      message
      objectRef
      success
    }
  }
`)

const declineQuoteMutation = gql(/*GraphQL*/ `
  mutation QuoteDecline($quoteKey: Key! $reason: String!) {
    quoteDecline(quoteKey: $quoteKey, reason: $reason) {
      ... on QuoteMutationResponse {
        code
        message
        success
        quote {
          ...LegalMatterQuote
        }
      }
      ... on MutationError {
        code
        message
        success
        code
        userErrors {
          fieldErrors {
            name
            errors {
              message
              code
            }
          }
        }
      }
    }
  }
`)

const sendQuoteMutation = gql(/*GraphQL*/ `
  mutation sendQuote($quoteKey: Key!) {
    quoteSend(quoteKey: $quoteKey) {
      ... on QuoteMutationResponse {
        code
        message
        success
        quote {
          ...LegalMatterQuote
        }
      }
      ... on MutationError {
        code
        message
        success
        code
        userErrors {
          fieldErrors {
            name
            errors {
              message
              code
            }
          }
        }
      }
    }
  }
`)

const updateQuoteMutation = gql(/*GraphQL*/ `
  mutation updateQuote($quoteData: QuoteUpdateInput!) {
    quoteUpdate(quoteData: $quoteData) {
      ... on QuoteMutationResponse {
        code
        message
        success
        quote {
          ...LegalMatterQuote
        }
      }
      ... on MutationError {
        code
        message
        success
        code
        userErrors {
          fieldErrors {
            name
            errors {
              message
              code
            }
          }
        }
      }
    }
  }
`)

interface QuotesHook {
  acceptQuote: (
    mutationOpts:
      | MutationFunctionOptions<
          AcceptQuoteMutation,
          OperationVariables,
          DefaultContext,
          ApolloCache<any>
        >
      | undefined,
  ) => Promise<FetchResult<AcceptQuoteMutation>>
  createQuote: (
    mutationOpts:
      | MutationFunctionOptions<
          CreateQuoteMutation,
          OperationVariables,
          DefaultContext,
          ApolloCache<any>
        >
      | undefined,
  ) => Promise<FetchResult<CreateQuoteMutation>>
  deleteQuote: (
    mutationOpts:
      | MutationFunctionOptions<
          DeleteQuoteMutation,
          OperationVariables,
          DefaultContext,
          ApolloCache<any>
        >
      | undefined,
  ) => Promise<FetchResult<DeleteQuoteMutation>>
  declineQuote: (
    mutationOpts:
      | MutationFunctionOptions<
          QuoteDeclineMutation,
          OperationVariables,
          DefaultContext,
          ApolloCache<any>
        >
      | undefined,
  ) => Promise<FetchResult<QuoteDeclineMutation>>
  sendQuote: (
    mutationOpts:
      | MutationFunctionOptions<
          SendQuoteMutation,
          OperationVariables,
          DefaultContext,
          ApolloCache<any>
        >
      | undefined,
  ) => Promise<FetchResult<SendQuoteMutation>>
  updateQuote: (
    mutationOpts:
      | MutationFunctionOptions<
          UpdateQuoteMutation,
          OperationVariables,
          DefaultContext,
          ApolloCache<any>
        >
      | undefined,
  ) => Promise<FetchResult<UpdateQuoteMutation>>
  accepting: boolean
  acceptError: any
  creating: boolean
  createError: any
  deleting: boolean
  deleteError: any
  rejecting: boolean
  rejectError: any
  sending: boolean
  sendError: any
  updating: boolean
  updateError: any
  quoteLoading: boolean
}

const useQuotes = (legalMatterKey: string | undefined): QuotesHook => {
  const [acceptQuote, { loading: accepting, error: acceptError }] =
    useMutation<AcceptQuoteMutation>(acceptQuoteMutation)

  const [createQuote, { loading: creating, error: createError }] =
    useMutation<CreateQuoteMutation>(createQuoteMutation, {
      update(cache, { data }) {
        if (
          legalMatterKey != null &&
          data?.quoteCreate?.__typename === 'QuoteMutationResponse'
        ) {
          const quote: LegalMatterQuoteFragment = data.quoteCreate.quote
          cache.modify({
            id: getCacheId('LegalMatter', legalMatterKey),
            fields: {
              quotes(
                existingQuoteConnection = makeEmptyData(),
                { toReference },
              ) {
                const newQuoteRef = toReference(quote)
                if (!edgesInclude(existingQuoteConnection.edges, newQuoteRef)) {
                  return {
                    ...existingQuoteConnection,
                    edges: [
                      ...existingQuoteConnection.edges,
                      {
                        __typename: 'QuoteEdge',
                        node: newQuoteRef,
                      },
                    ],
                  } as AsStoreObject<QuoteConnection>
                } else {
                  return existingQuoteConnection
                }
              },
            },
          })
        }
      },
    })

  const [deleteQuote, { loading: deleting, error: deleteError }] =
    useMutation<DeleteQuoteMutation>(deleteQuoteMutation, {
      update(cache, { data }) {
        if (legalMatterKey != null && data?.quoteDelete.success) {
          cache.modify({
            id: getCacheId('LegalMatter', legalMatterKey),
            fields: {
              quotes(existingQuoteConnection = {}) {
                return {
                  ...existingQuoteConnection,
                  edges: filter(
                    existingQuoteConnection.edges,
                    (edge) =>
                      edge.node.__ref !=
                      getCacheId('Quote', data.quoteDelete.objectRef),
                  ),
                } as QuoteConnection
              },
            },
          })
          cache.evict({ id: getCacheId('Quote', data.quoteDelete.objectRef) })
        }
      },
    })

  const [declineQuote, { loading: declining, error: declineError }] =
    useMutation<QuoteDeclineMutation>(declineQuoteMutation)

  const [sendQuote, { loading: sending, error: sendError }] =
    useMutation<SendQuoteMutation>(sendQuoteMutation)

  const [updateQuote, { loading: updating, error: updateError }] =
    useMutation<UpdateQuoteMutation>(updateQuoteMutation)

  const quoteLoading =
    accepting || creating || deleting || declining || sending || updating

  return {
    acceptQuote,
    createQuote,
    deleteQuote,
    declineQuote,
    sendQuote,
    updateQuote,
    accepting,
    acceptError,
    creating,
    createError,
    deleting,
    deleteError,
    declining,
    declineError,
    sending,
    sendError,
    updating,
    updateError,
    quoteLoading,
  }
}

export default useQuotes
